import React, {
  useEffect,
  useState,
  createContext,
  useMemo,
  SetStateAction,
} from 'react'
import { useSelector } from 'react-redux'
import * as amplitude from '@amplitude/analytics-browser'
import { Experiment, ExperimentClient } from '@amplitude/experiment-js-client'
import FingerprintJS from '@fingerprintjs/fingerprintjs'

import {
  getUserIdFromToken,
  getUserTypeFromToken,
  getUserEmailFromToken,
} from 'src/auth'
import { selectClientId } from 'src/store/selectors/client'
import {
  selectCorporateProfileId,
  selectCustomerId,
} from 'src/store/selectors/customerProfile'

// TODO: env
const amplitudeApiKey = '34d62f62d612364e0320590ec0b5039b'
const amplitudeClientsDeploymentKey = 'client-9vbKfF0SS20nwPgM9JjDcj1RAxhfFZ18'

interface utmFieldTypes {
  utm_id?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
}

const defaultExp: {
  amplitude?: any
  experiment?: ExperimentClient
  expUserId?: string
  setExpUserId: React.Dispatch<SetStateAction<string | null>>
  setExpUserUTMFields: React.Dispatch<SetStateAction<utmFieldTypes>>
} = {
  amplitude: undefined,
  experiment: undefined,
  setExpUserId: () => {
    console.warn('ExpProvider has not yet mounted')
  },
  setExpUserUTMFields: () => {
    console.warn('ExpProvider has not yet mounted')
  },
}

const fpPromise = FingerprintJS.load()
export const ExpContext = createContext(defaultExp)

const ExpProvider = ({ children }) => {
  const [experiment, setExperiment] = useState(defaultExp.experiment)
  const [expUserId, setExpUserId] = useState(getUserIdFromToken())
  const [expUserUTMFields, setExpUserUTMFields] = useState({})

  const clientId = useSelector(selectClientId)
  const customerId = useSelector(selectCustomerId)
  const corpId = useSelector(selectCorporateProfileId)

  const userFields = useMemo(() => {
    const userEmail = getUserEmailFromToken()

    return {
      clientId,
      customerId,
      corpId,
      emailDomain: userEmail.replace(/^.+?@(.*?)/, '$1'),
      emailTag:
        userEmail.indexOf('+') > -1
          ? userEmail.replace(/^.+?\+([^+@]*)@.+$/, '$1')
          : '',
      userType: getUserTypeFromToken(),
      ...expUserUTMFields,
    }
  }, [clientId, customerId, corpId, expUserUTMFields])

  // Init stuff
  useEffect(() => {
    if (expUserId) {
      const initAndFetchClient = async () => {
        amplitude.init(amplitudeApiKey, expUserId, {
          attribution: {
            trackPageViews: true,
            trackNewCampaigns: true,
          },
        })
        const exp = Experiment.initializeWithAmplitudeAnalytics(
          amplitudeClientsDeploymentKey
        )
        const fp = await fpPromise
        const fpResult = await fp.get()
        await exp.fetch({ user_id: expUserId, device_id: fpResult.visitorId })
        setExperiment(exp)
      }

      initAndFetchClient()

      return () => {
        if (experiment) {
          amplitude.reset()
        }
      }
    }
    return () => true
  }, [expUserId])

  // Keep user properties up to date
  useEffect(() => {
    if (expUserId) {
      const identifyObj = new amplitude.Identify()
      Object.keys(userFields).forEach(key =>
        identifyObj.set(key, userFields[key])
      )
      amplitude.identify(identifyObj)
    }
  }, [expUserId, userFields])

  return (
    <ExpContext.Provider
      value={{
        amplitude,
        experiment,
        expUserId,
        setExpUserId,
        setExpUserUTMFields,
      }}
    >
      {children}
    </ExpContext.Provider>
  )
}

export default ExpProvider
